import api from "../../../utils/api";

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 7;
const WINDOW_MS = WINDOW_DAYS * DAY_MS;

export const CHUNK_CONCURRENCY = 4;
export const DEVICE_CONCURRENCY = 5;

interface RequestErrorShape {
  code?: string;
  message?: string;
  response?: { status?: number; statusText?: string; data?: unknown };
}

const describeRequestError = (error: unknown): string => {
  const err = error as RequestErrorShape;
  if (err?.response) {
    const raw = err.response.data;
    const body = raw == null ? "sin body" : typeof raw === "string" ? raw : JSON.stringify(raw);
    return `HTTP ${err.response.status ?? "?"} ${err.response.statusText ?? ""} · ${body.slice(0, 300)}`;
  }
  if (err?.code) return `${err.code} · ${err.message ?? "sin mensaje"}`;
  return err?.message ?? String(error);
};

export interface RawHistoryPoint {
  ts: number;
  lat: number;
  lng: number;
  speed?: number | null;
  heading?: number | null;
  ignition?: boolean | null;
  battery?: number | null;
  altitude?: number | null;
  event_code?: number | null;
  brand_name?: string | null;
  brand_model?: string | null;
  params?:
    | ({
        engine_status?: number | null;
        gps_sat?: number | null;
        odo_km?: number | string | null;
        temperature?: number | string | null;
        dallas_temperature?: number | string | null;
        rpm?: number | string | null;
      } & Record<string, number | string | null | undefined>)
    | null;
}

export interface DateWindow {
  from: string;
  to: string;
}

export interface WindowResult {
  points: RawHistoryPoint[];
  addresses?: Record<string, string>;
  error: boolean;
  errorDetail?: string;
}

export const buildWindows = (fromMs: number, toMs: number): DateWindow[] => {
  const windows: DateWindow[] = [];
  let cursor = fromMs;
  while (cursor <= toMs) {
    const end = Math.min(cursor + WINDOW_MS, toMs);
    windows.push({
      from: new Date(cursor).toISOString(),
      to: new Date(end).toISOString(),
    });
    cursor = end + 1;
  }
  return windows;
};

export const processInBatches = async <T, R>(
  items: T[],
  batchSize: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> => {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(fn));
    results.push(...batchResults);
  }
  return results;
};

export const fetchHistoryWindow = async (
  deviceId: string,
  window: DateWindow,
  tz: string,
  address = false,
): Promise<WindowResult> => {
  try {
    const res = await api.get(
      `/tracking/history/${encodeURIComponent(deviceId)}`,
      {
        params: {
          from: window.from,
          to: window.to,
          tz,
          address: address ? "true" : undefined,
        },
      },
    );
    const payload = res.data?.data ?? res.data;
    return {
      points: (payload?.points ?? []) as RawHistoryPoint[],
      addresses: payload?.addresses,
      error: false,
    };
  } catch (error) {
    return { points: [], error: true, errorDetail: describeRequestError(error) };
  }
};

// event_code de las posiciones periódicas: 6 position, 26 on_motion, 27
// off_motion. Mapon y Wialon mandan 1 en todas sus posiciones. Mismo criterio
// que isRealEvent de apps/torre-de-control-api/functions/src/utils/ReportUtils.js.
const POSITION_EVENT_CODES = new Set([6, 26, 27]);
export const isRealEvent = (p: RawHistoryPoint): boolean => {
  const code = toNumber(p.event_code ?? p.params?.event_code ?? null);
  if (code === null || POSITION_EVENT_CODES.has(code)) return false;
  const provider = p.params?.provider;
  return !(code === 1 && (provider === "mapon" || provider === "wialon"));
};

const fillFrom = (win: RawHistoryPoint, other: RawHistoryPoint): RawHistoryPoint => {
  const out: Record<string, unknown> = { ...win };
  for (const [k, v] of Object.entries(other)) if (out[k] == null) out[k] = v;
  if (win.params && other.params) {
    const params: Record<string, unknown> = { ...win.params };
    for (const [k, v] of Object.entries(other.params)) if (params[k] == null) params[k] = v;
    out.params = params;
  }
  return out as unknown as RawHistoryPoint;
};

// Gravedad cuando dos eventos reales caen en el mismo segundo. Misma tabla que
// EVENT_PRIORITY de ReportUtils.js (allá la evidencia: 115 pánicos perdidos).
const EVENT_PRIORITY = new Map<number, number>([
  [5, 100], // pánico
  [18, 90], // desconexión de energía
  [36, 80], // jamming
  [1, 60], [34, 60], [35, 60], // exceso, aceleración y frenado bruscos
  [23, 50], [24, 50], // entrada y salida de zona
  [3, 20], [4, 20], // encendido y apagado
]);
export const eventPriority = (p: RawHistoryPoint): number =>
  EVENT_PRIORITY.get(toNumber(p.event_code ?? p.params?.event_code ?? null) ?? NaN) ?? 40;

/**
 * Dos puntos con el mismo ts: si sólo uno es un evento real queda ése, con lo
 * que le falte completado desde el otro; si los dos lo son, el más grave; si
 * no, el último (como antes). Así un evento registrado en el mismo segundo que
 * una posición u otro evento menor no se pierde.
 */
export const mergeSameTs = (prev: RawHistoryPoint, cur: RawHistoryPoint): RawHistoryPoint => {
  const prevReal = isRealEvent(prev);
  if (prevReal !== isRealEvent(cur)) return prevReal ? fillFrom(prev, cur) : fillFrom(cur, prev);
  if (prevReal && eventPriority(prev) > eventPriority(cur)) return fillFrom(prev, cur);
  return cur;
};

export const loadDeviceHistory = async (
  deviceId: string,
  windows: DateWindow[],
  tz: string,
  address = false,
): Promise<{
  points: RawHistoryPoint[];
  addresses: Record<string, string>;
  failedChunks: number;
  errorDetails: string[];
}> => {
  const chunkResults = await processInBatches(windows, CHUNK_CONCURRENCY, (w) =>
    fetchHistoryWindow(deviceId, w, tz, address),
  );

  const failedChunks = chunkResults.filter((c) => c.error).length;
  const errorDetails = chunkResults
    .map((c) => c.errorDetail)
    .filter((d) => Boolean(d)) as string[];

  const addresses: Record<string, string> = {};
  const byTs = new Map<number, RawHistoryPoint>();

  chunkResults.forEach((chunk) => {
    if (chunk.addresses) Object.assign(addresses, chunk.addresses);
    chunk.points.forEach((p) => {
      if (p.ts == null) return;
      const prev = byTs.get(p.ts);
      byTs.set(p.ts, prev ? mergeSameTs(prev, p) : p);
    });
  });

  const points = Array.from(byTs.values()).sort((a, b) => a.ts - b.ts);
  return { points, addresses, failedChunks, errorDetails };
};

export const toNumber = (
  value: number | string | null | undefined,
): number | null => {
  if (value == null) return null;
  const num = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(num) ? num : null;
};

export interface RawTrackingEvent {
  ts: number;
  lat: number;
  lng: number;
  event_code?: number | null;
  event_type?: string | null;
  value?: number | string | null;
  geofence_id?: string | null;
  trip_id?: string | null;
  metadata?: Record<string, unknown> | null;
  params?: Record<string, number | string | null | undefined> | null;
}

interface EventsWindowResult {
  events: RawTrackingEvent[];
  addresses?: Record<string, string>;
  error: boolean;
  errorDetail?: string;
}

const fetchEventsWindow = async (
  deviceId: string,
  window: DateWindow,
  tz: string,
  address: boolean,
): Promise<EventsWindowResult> => {
  try {
    const res = await api.get(
      `/tracking/device-events/${encodeURIComponent(deviceId)}`,
      {
        params: {
          from: window.from,
          to: window.to,
          tz,
          address: address ? "true" : undefined,
        },
      },
    );
    const payload = res.data?.data ?? res.data;
    return {
      events: (payload?.events ?? []) as RawTrackingEvent[],
      addresses: payload?.addresses,
      error: false,
    };
  } catch (error) {
    return { events: [], error: true, errorDetail: describeRequestError(error) };
  }
};

export const eventDedupeKey = (e: RawTrackingEvent): string =>
  `${e.ts}-${e.event_code ?? ""}-${e.event_type ?? ""}`;

export const dedupeEvents = (events: RawTrackingEvent[]): RawTrackingEvent[] => {
  const byKey = new Map<string, RawTrackingEvent>();
  events.forEach((e) => {
    if (e.ts != null) byKey.set(eventDedupeKey(e), e);
  });
  return Array.from(byKey.values()).sort((a, b) => a.ts - b.ts);
};

export const loadDeviceEvents = async (
  deviceId: string,
  windows: DateWindow[],
  tz: string,
  address = false,
): Promise<{
  events: RawTrackingEvent[];
  addresses: Record<string, string>;
  failedChunks: number;
  errorDetails: string[];
}> => {
  const chunkResults = await processInBatches(windows, CHUNK_CONCURRENCY, (w) =>
    fetchEventsWindow(deviceId, w, tz, address),
  );

  const failedChunks = chunkResults.filter((c) => c.error).length;
  const errorDetails = chunkResults
    .map((c) => c.errorDetail)
    .filter((d) => Boolean(d)) as string[];

  const addresses: Record<string, string> = {};

  chunkResults.forEach((chunk) => {
    if (chunk.addresses) Object.assign(addresses, chunk.addresses);
  });

  const events = dedupeEvents(chunkResults.flatMap((chunk) => chunk.events));
  return { events, addresses, failedChunks, errorDetails };
};
